import {
  deleteDoc,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
  type DocumentData,
} from 'firebase/firestore'
import { firebaseAuth, firestoreDb } from './firebase'
import {
  createEmptyCognitiveProfile,
  type CognitiveProfile,
  type CognitiveProfilePatch,
  type Constraint,
  type MemoryUpdate,
  type Outcome,
  type OutcomeInput,
  type UserRule,
  type UserRuleInput,
  type WorkStyle,
} from '../types/cognitiveProfile'

const COLLECTION_NAME = 'user_cognitive_profiles'
const HISTORY_LIMIT = 50
const MUTATION_ID_LIMIT = 50
export const MEMORY_FLUSH_DELAY_MS = 2_000
const memoryBeaconUrl = import.meta.env.VITE_MEMORY_BEACON_URL as string | undefined

const assertAuthenticatedOwner = (uid: string) => {
  const user = firebaseAuth.currentUser
  if (!uid || !user || user.isAnonymous || user.uid !== uid) {
    throw new Error('Cognitive memory requires the matching authenticated user.')
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const mergeRecords = (
  current: Record<string, unknown>,
  incoming: Record<string, unknown>,
): Record<string, unknown> => {
  const merged = { ...current }
  Object.entries(incoming).forEach(([key, value]) => {
    merged[key] = isRecord(value) && isRecord(current[key])
      ? mergeRecords(current[key] as Record<string, unknown>, value)
      : value
  })
  return merged
}

const normalizeProfile = (uid: string, data?: DocumentData): CognitiveProfile => {
  const defaults = createEmptyCognitiveProfile(uid)
  if (!data) return defaults
  return {
    uid,
    preferences: isRecord(data.preferences) ? data.preferences : {},
    workStyle: {
      ...defaults.workStyle,
      ...(isRecord(data.workStyle) ? data.workStyle : {}),
    } as WorkStyle,
    constraints: Array.isArray(data.constraints) ? data.constraints as Constraint[] : [],
    userRules: Array.isArray(data.userRules) ? data.userRules as UserRule[] : [],
    projectHistory: Array.isArray(data.projectHistory)
      ? (data.projectHistory as Outcome[]).slice(-HISTORY_LIMIT)
      : [],
    lastUpdatedAt: data.lastUpdatedAt instanceof Timestamp ? data.lastUpdatedAt : null,
  }
}

const isOutcomeInput = (update: MemoryUpdate): update is OutcomeInput =>
  typeof (update as OutcomeInput).summary === 'string'
  && typeof (update as OutcomeInput).outcomeType === 'string'

const createOutcome = (input: OutcomeInput): Outcome => ({
  id: crypto.randomUUID(),
  timestamp: Timestamp.now(),
  summary: input.summary,
  outcomeType: input.outcomeType,
})

const normalizeUpdate = (update: MemoryUpdate): CognitiveProfilePatch => {
  const unsafe = update as MemoryUpdate & { uid?: unknown; lastUpdatedAt?: unknown }
  if ('uid' in unsafe || 'lastUpdatedAt' in unsafe) {
    throw new Error('Cognitive profile identity and lastUpdatedAt are immutable.')
  }
  if (isOutcomeInput(update)) return { projectHistory: [update] }
  return update
}

const appendHistory = (current: Outcome[], incoming: OutcomeInput[]): Outcome[] => {
  const byId = new Map(current.map((outcome) => [outcome.id, outcome]))
  incoming.map(createOutcome).forEach((outcome) => byId.set(outcome.id, outcome))
  return Array.from(byId.values())
    .sort((left, right) => left.timestamp.toMillis() - right.timestamp.toMillis())
    .slice(-HISTORY_LIMIT)
}

const applyUpdates = (profile: CognitiveProfile, updates: CognitiveProfilePatch[]): CognitiveProfile =>
  updates.reduce<CognitiveProfile>((current, update) => ({
    ...current,
    preferences: update.preferences
      ? mergeRecords(current.preferences, update.preferences)
      : current.preferences,
    workStyle: update.workStyle
      ? { ...current.workStyle, ...update.workStyle }
      : current.workStyle,
    constraints: update.constraints ?? current.constraints,
    userRules: update.userRules ?? current.userRules,
    projectHistory: update.projectHistory
      ? appendHistory(current.projectHistory, update.projectHistory)
      : current.projectHistory,
  }), profile)

type PendingWrite = {
  mutationId: string
  updates: CognitiveProfilePatch[]
  timer: ReturnType<typeof setTimeout>
  resolve: Array<() => void>
  reject: Array<(error: unknown) => void>
}

const pendingWrites = new Map<string, PendingWrite>()
type MemoryMutationEnvelope = { mutationId: string; updates: CognitiveProfilePatch[] }
const inFlightWrites = new Map<string, { promise: Promise<void>; envelope: MemoryMutationEnvelope }>()
const deletingUsers = new Set<string>()
const beaconTokens = new Map<string, string>()

const persistUpdates = async (uid: string, envelope: MemoryMutationEnvelope) => {
  const profileRef = doc(firestoreDb, COLLECTION_NAME, uid)
  await runTransaction(firestoreDb, async (transaction) => {
    const snapshot = await transaction.get(profileRef)
    const recentMutationIds: string[] = snapshot.exists() && Array.isArray(snapshot.data().recentMemoryMutationIds)
      ? (snapshot.data().recentMemoryMutationIds as unknown[]).filter((value): value is string => typeof value === 'string')
      : []
    if (recentMutationIds.includes(envelope.mutationId)) return
    const current = normalizeProfile(uid, snapshot.exists() ? snapshot.data() : undefined)
    const next = applyUpdates(current, envelope.updates)
    transaction.set(profileRef, {
      ...next,
      uid,
      lastUpdatedAt: serverTimestamp(),
      recentMemoryMutationIds: [...recentMutationIds, envelope.mutationId].slice(-MUTATION_ID_LIMIT),
    })
  })
}

const flushNext = async (uid: string): Promise<void> => {
  const active = inFlightWrites.get(uid)
  if (active) {
    await active.promise
    return flushNext(uid)
  }

  const pending = pendingWrites.get(uid)
  if (!pending) return
  pendingWrites.delete(uid)
  clearTimeout(pending.timer)

  const envelope = { mutationId: pending.mutationId, updates: pending.updates }
  const operation = persistUpdates(uid, envelope)
  inFlightWrites.set(uid, { promise: operation, envelope })
  try {
    await operation
    pending.resolve.forEach((resolve) => resolve())
  } catch (error) {
    pending.reject.forEach((reject) => reject(error))
    throw error
  } finally {
    inFlightWrites.delete(uid)
  }

  return flushNext(uid)
}

export const flushMemory = async (uid: string): Promise<void> => {
  assertAuthenticatedOwner(uid)
  await flushNext(uid)
}

export const upsertMemory = (uid: string, update: MemoryUpdate): Promise<void> => {
  assertAuthenticatedOwner(uid)
  if (deletingUsers.has(uid)) {
    return Promise.reject(new Error('Cognitive memory is being deleted.'))
  }
  const normalized = normalizeUpdate(update)
  const user = firebaseAuth.currentUser
  if (user?.uid === uid && typeof user.getIdToken === 'function') {
    void user.getIdToken()
      .then((token) => beaconTokens.set(uid, token))
      .catch((error) => console.error('[memory] could not cache teardown token', error))
  }

  return new Promise<void>((resolve, reject) => {
    const existing = pendingWrites.get(uid)
    if (existing) {
      clearTimeout(existing.timer)
      existing.updates.push(normalized)
      existing.resolve.push(resolve)
      existing.reject.push(reject)
      existing.timer = setTimeout(() => {
        void flushNext(uid).catch(() => undefined)
      }, MEMORY_FLUSH_DELAY_MS)
      return
    }

    const pending: PendingWrite = {
      mutationId: crypto.randomUUID(),
      updates: [normalized],
      resolve: [resolve],
      reject: [reject],
      timer: setTimeout(() => {
        void flushNext(uid).catch(() => undefined)
      }, MEMORY_FLUSH_DELAY_MS),
    }
    pendingWrites.set(uid, pending)
  })
}

export const getMemory = async (uid: string): Promise<CognitiveProfile> => {
  assertAuthenticatedOwner(uid)
  const snapshot = await getDoc(doc(firestoreDb, COLLECTION_NAME, uid))
  return normalizeProfile(uid, snapshot.exists() ? snapshot.data() : undefined)
}

export const deleteMemory = async (uid: string): Promise<void> => {
  assertAuthenticatedOwner(uid)
  deletingUsers.add(uid)
  const pending = pendingWrites.get(uid)
  if (pending) {
    clearTimeout(pending.timer)
    pendingWrites.delete(uid)
    const error = new Error('Pending cognitive memory update was discarded by deleteMemory.')
    pending.reject.forEach((reject) => reject(error))
  }
  try {
    const active = inFlightWrites.get(uid)
    if (active) await active.promise
    await deleteDoc(doc(firestoreDb, COLLECTION_NAME, uid))
  } finally {
    deletingUsers.delete(uid)
  }
}

const flushVisibleMemory = () => {
  pendingWrites.forEach((_pending, uid) => {
    void flushNext(uid).catch((error) => console.error('[memory] visibility flush failed', error))
  })
}

const sendMemoryBeacon = () => {
  if (pendingWrites.size === 0 && inFlightWrites.size === 0) return
  if (!memoryBeaconUrl || typeof navigator.sendBeacon !== 'function') {
    console.error('[memory] teardown beacon is not configured')
    return
  }
  const byUid = new Map<string, MemoryMutationEnvelope[]>()
  inFlightWrites.forEach(({ envelope }, uid) => byUid.set(uid, [envelope]))
  pendingWrites.forEach((pending, uid) => {
    const envelopes = byUid.get(uid) ?? []
    envelopes.push({ mutationId: pending.mutationId, updates: pending.updates })
    byUid.set(uid, envelopes)
  })
  byUid.forEach((mutations, uid) => {
    const idToken = beaconTokens.get(uid)
    if (!idToken) {
      console.error('[memory] teardown beacon skipped because no auth token was cached')
      return
    }
    const body = JSON.stringify({ idToken, memoryDelta: { mutations } })
    const queued = navigator.sendBeacon(
      memoryBeaconUrl,
      new Blob([body], { type: 'text/plain;charset=UTF-8' }),
    )
    if (!queued) console.error('[memory] browser rejected the teardown beacon')
  })
}

const USER_RULE_LIMIT = 50
const truncateRuleText = (text: string) => text.trim().slice(0, 400)

const createUserRule = (input: UserRuleInput): UserRule => ({
  id: crypto.randomUUID(),
  text: truncateRuleText(input.text),
  scope: input.scope,
  source: input.source,
  createdAt: Timestamp.now(),
})

/**
 * Append a user rule to the cognitive profile. Loads current state so
 * `applyUpdates` (which replaces the array) doesn't clobber prior rules.
 * Deduped by trimmed text — resaving an identical rule is a no-op.
 */
export const appendUserRule = async (uid: string, input: UserRuleInput): Promise<UserRule | null> => {
  assertAuthenticatedOwner(uid)
  const normalized = truncateRuleText(input.text)
  if (!normalized) return null
  const current = await getMemory(uid)
  if (current.userRules.some((rule) => rule.text.toLowerCase() === normalized.toLowerCase())) return null
  const nextRule = createUserRule({ ...input, text: normalized })
  const nextRules = [...current.userRules, nextRule].slice(-USER_RULE_LIMIT)
  await upsertMemory(uid, { userRules: nextRules })
  return nextRule
}

export const removeUserRule = async (uid: string, ruleId: string): Promise<void> => {
  assertAuthenticatedOwner(uid)
  const current = await getMemory(uid)
  const nextRules = current.userRules.filter((rule) => rule.id !== ruleId)
  if (nextRules.length === current.userRules.length) return
  await upsertMemory(uid, { userRules: nextRules })
}

export const updateUserRule = async (
  uid: string,
  ruleId: string,
  patch: Partial<Pick<UserRule, 'text' | 'scope'>>,
): Promise<void> => {
  assertAuthenticatedOwner(uid)
  const current = await getMemory(uid)
  const nextRules = current.userRules.map((rule) => rule.id === ruleId
    ? {
        ...rule,
        text: patch.text !== undefined ? truncateRuleText(patch.text) : rule.text,
        scope: patch.scope ?? rule.scope,
      }
    : rule)
  if (nextRules.every((rule) => rule.text.length > 0)) {
    await upsertMemory(uid, { userRules: nextRules })
  }
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushVisibleMemory()
  })
  window.addEventListener('pagehide', sendMemoryBeacon)
}
